import { create } from 'zustand';
import api, { setEnforcementHandler, setSessionLostHandler } from '../lib/api';
import { saveEnforcement, type EnforcementInfo } from '../lib/enforcement';
import { User, GlobalRole } from '../types';
import { useNotificationStore } from './notificationStore';

const ACCOUNT_SUSPENDED_PATH = '/account-suspended';

export type RegisterResult = {
  user: User | null;
  needsEmailConfirmation: boolean;
  message?: string;
};

export type MfaFactor = {
  id: string;
  type: string;
  friendlyName: string | null;
  status: string;
};

export type LoginResult =
  | { requiresMfa: false; user: User }
  | { requiresMfa: true; factors: MfaFactor[] };

interface AuthState {
  user: User | null;
  isLoading: boolean;
  hasCheckedSession: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  demoLogin: (email: string) => Promise<LoginResult>;
  verifyMfa: (code: string, factorId?: string) => Promise<User>;
  completeEmailConfirmation: (accessToken: string) => Promise<void>;
  completeRecovery: (accessToken: string, newPassword: string) => Promise<void>;
  register: (data: any) => Promise<RegisterResult>;
  completeSocialSignup: (role: GlobalRole) => Promise<User>;
  logout: () => Promise<void>;
  fetchUser: (options?: { silent?: boolean }) => Promise<void>;
  updateUser: (user: User) => void;
  /** API answered 403 with an enforcement code: stash it and hard-navigate to /account-suspended. */
  handleEnforcement: (info: EnforcementInfo) => void;
  /** Session revoked / refresh failed: drop the local user so ProtectedRoute redirects to /login. */
  handleSessionLost: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  hasCheckedSession: false,
  isAuthenticated: false,

  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.requiresMfa) {
      set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
      return { requiresMfa: true, factors: response.data.factors ?? [] };
    }
    const user = response.data.user as User;
    set({ user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
    useNotificationStore.getState().resetAuthGate();
    return { requiresMfa: false, user };
  },

  demoLogin: async (email: string) => {
    const response = await api.post('/auth/demo-login', { email });
    if (response.data.requiresMfa) {
      set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
      return { requiresMfa: true, factors: response.data.factors ?? [] };
    }
    const user = response.data.user as User;
    set({ user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
    useNotificationStore.getState().resetAuthGate();
    return { requiresMfa: false, user };
  },

  verifyMfa: async (code: string, factorId?: string) => {
    const response = await api.post<{ user: User }>('/auth/mfa/verify', { code, factorId });
    const user = response.data.user as User;
    set({ user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
    useNotificationStore.getState().resetAuthGate();
    return user;
  },

  completeEmailConfirmation: async (accessToken: string) => {
    const response = await api.post('/auth/confirm-email', { access_token: accessToken });
    set({ user: response.data.user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
    useNotificationStore.getState().resetAuthGate();
  },

  completeRecovery: async (accessToken: string, newPassword: string) => {
    const response = await api.post('/auth/complete-recovery', {
      access_token: accessToken,
      newPassword,
    });
    set({ user: response.data.user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
    useNotificationStore.getState().resetAuthGate();
  },

  register: async (data: any) => {
    const response = await api.post('/auth/register', data);
    const user = (response.data.user ?? null) as User | null;
    const needsEmailConfirmation = Boolean(response.data.needsEmailConfirmation);
    const message = typeof response.data.message === 'string' ? response.data.message : undefined;

    if (user) {
      set({ user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
      useNotificationStore.getState().resetAuthGate();
    } else {
      set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
    }

    return { user, needsEmailConfirmation, message };
  },

  completeSocialSignup: async (role: GlobalRole) => {
    const response = await api.post<{ user: User }>('/auth/social-signup/complete', { role });
    const user = response.data.user as User;
    set({ user, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
    useNotificationStore.getState().resetAuthGate();
    return user;
  },

  logout: async () => {
    await api.post('/auth/logout');
    useNotificationStore.getState().resetAuthGate();
    set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
  },

  fetchUser: async (options) => {
    try {
      if (!options?.silent) set({ isLoading: true });
      const response = await api.get('/auth/me');
      set({ user: response.data, isAuthenticated: true, isLoading: false, hasCheckedSession: true });
      useNotificationStore.getState().resetAuthGate();
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
    }
  },

  updateUser: (user: User) => {
    set({ user });
  },

  handleEnforcement: (info: EnforcementInfo) => {
    if (typeof window === 'undefined') return;
    // Loop guard: a 403 storm from parallel requests must not re-trigger the navigation.
    if (window.location.pathname === ACCOUNT_SUSPENDED_PATH) return;
    saveEnforcement(info);
    set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
    // Hard navigation (not the router): cancels in-flight requests and unmounts
    // RealtimeBridge/HelpChatWidget so nothing keeps polling the API.
    window.location.replace(ACCOUNT_SUSPENDED_PATH);
  },

  handleSessionLost: () => {
    const { user, isAuthenticated } = get();
    if (!user && !isAuthenticated) return;
    useNotificationStore.getState().resetAuthGate();
    set({ user: null, isAuthenticated: false, isLoading: false, hasCheckedSession: true });
  },
}));

// Registered here (not in api.ts) to avoid the api <-> authStore import cycle.
setEnforcementHandler((info) => useAuthStore.getState().handleEnforcement(info));
setSessionLostHandler(() => useAuthStore.getState().handleSessionLost());

